import {
  BarChart,
  Bar,
  Brush,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";

import { useMediaQuery } from "react-responsive";
import { ChartWrapper } from "../../components/ChartWrapper";
import { formatNumber } from "../../../utils/number";
import { formatLabel } from "../../../utils/string";

/* -------- TOTAL -------- */
const getTotal = (data = []) =>
  data.reduce(
    (sum, item) =>
      sum +
      (item?.Correct || 0) +
      (item?.Incorrect || 0) +
      (item?.Unattempted || 0),
    0
  );

/* -------- LABEL -------- */
const renderLabel = (key, isSmall) => (props) => {
  const { x, y, width, height, value } = props;

  if (!value) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 4}
      textAnchor="middle"
      fill="#111"
      fontSize={isSmall ? 9 : 11}
      fontWeight="600"
    >
      {formatNumber(value)}
    </text>
  );
};

export default function ResourceWiseQuestionAttemptAnalysis({ data = [], loading = false }) {
  const isSmall = useMediaQuery({ query: "(max-width: 600px)" });

  const isEmpty = !data || data.length === 0;
  const total = getTotal(data);

  return (
    <ChartWrapper
      loading={loading}
      isEmpty={isEmpty}
      title="Question Attempts Analysis"
      description={`Resource-wise breakdown of correct, incorrect, and unattempted questions (Total: ${formatNumber(
        total
      )})`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          barCategoryGap={data.length === 1 ? "40%":isSmall ? "15%" : "30%"}
          barGap={0}
          margin={{
            top: 30,
            right: 20,
            left: isSmall ? 0 : 10,
            bottom: isSmall ? 40 : 60,
          }}
        >
          {/* GRID */}
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {/* X AXIS */}
          <XAxis
            dataKey="Subject"
            tickFormatter={(v) => formatLabel(v)}
            interval={0}
            tick={{
              fontSize: isSmall ? 11 : 13,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#999", strokeWidth: 1.2 }}
            tickLine={false}
            angle={isSmall ? -25 : 0}
            textAnchor={isSmall ? "end" : "middle"}
          />

          {/* BRUSH */}
          <Brush
            dataKey="Subject"
            height={10}
            travellerWidth={8}
            stroke="#94a3b8"
            endIndex={Math.min(data.length - 1, 4)}
          />

          {/* Y AXIS */}
          <YAxis
            tickFormatter={(v) => formatNumber(v)}
            tick={{
              fontSize: 12,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#999", strokeWidth: 1.2 }}
            tickLine={false}
            width={50}
            label={{
              value: "Questions",
              angle: -90,
              position: "insideLeft",
              dx: isSmall ? -5 : -10,
              style: {
                textAnchor: "middle",
                fontSize: 12,
                fontWeight: 700,
                fill: "#111",
              },
            }}
          />

          {/* TOOLTIP */}
          <Tooltip
            contentStyle={{
              borderRadius: "10px",
              border: "none",
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              fontSize: "13px",
              fontWeight:500
            }}
          />

          {/* GROUPED BARS */}
          <Bar dataKey="Correct" fill="#22c55e" maxBarSize={50}>
            <LabelList content={renderLabel("Correct", isSmall)} />
          </Bar>

          <Bar dataKey="Incorrect" fill="#ef4444" maxBarSize={50}>
            <LabelList content={renderLabel("Incorrect", isSmall)} />
          </Bar>

          <Bar dataKey="Unattempted" fill="#94a3b8" maxBarSize={50}>
            <LabelList content={renderLabel("Unattempted", isSmall)} />
          </Bar>

          {/* LEGEND */}
          <Legend
            wrapperStyle={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: 10,
               height:"0px"
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}